import { useState, useEffect, useCallback } from 'react'
import { getPage, post, patch as patchReq, del as delReq } from '../lib/api'
import type { Resume } from '../types/api'

export function useResumes(talentId: string | undefined) {
  const [resumes, setResumes] = useState<Resume[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)

  const load = useCallback(async () => {
    if (!talentId) {
      setResumes([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await getPage<Resume>(
        `/api/v1/talents/${talentId}/resumes`,
      )
      setResumes(res.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : '알 수 없는 오류')
    } finally {
      setLoading(false)
    }
  }, [talentId])

  useEffect(() => {
    load()
  }, [load])

  const generate = useCallback(
    async (
      track: string,
      opts?: {
        packagingLevel?: number
        frameworks?: string[]
        forbiddenWords?: string[]
        requiredKeywords?: string[]
      },
    ) => {
      if (!talentId) return
      setGenerating(true)
      try {
        await post<Resume>(
          `/api/v1/talents/${talentId}/resumes/generate`,
          { track, ...opts },
        )
        await load()
      } catch (e) {
        throw e instanceof Error ? e : new Error('AI 생성 실패')
      } finally {
        setGenerating(false)
      }
    },
    [talentId, load],
  )

  const update = useCallback(
    async (
      id: string,
      data: { track?: string; content?: Record<string, unknown>; atsScore?: number },
    ) => {
      if (!talentId) return
      const body: Record<string, unknown> = {}
      if (data.track !== undefined) body.track = data.track
      if (data.content !== undefined) body.content = data.content
      if (data.atsScore !== undefined) body.ats_score = data.atsScore
      await patchReq(`/api/v1/talents/${talentId}/resumes/${id}`, body)
      await load()
    },
    [talentId, load],
  )

  const remove = useCallback(
    async (id: string) => {
      if (!talentId) return
      await delReq(`/api/v1/talents/${talentId}/resumes/${id}`)
      await load()
    },
    [talentId, load],
  )

  return { resumes, loading, error, reload: load, generate, generating, update, remove }
}
